import React from 'react'
import { IonGrid, IonRow, IonList, IonLabel } from '@ionic/react'
import HeaderSection from '../headerSection/HeaderSection.component'
import { useSelector } from 'react-redux'
import { getPcrSelector } from '../../home/selectors'
import { getTripInformationSelector } from '../../tripInformation/selectors'
import { getPatientDemographicsSelector } from '../../patientDemographics/selectors'
import { getUserSelector } from '../../user/selectors'
import { getAgeFromReference, getAgeUnitOptionIdFromReference } from '../../../utilities/dateTime.util'
import { getPatientDemographicsOptionsSelector } from '../../options/selectors'
import { getTimesSelector } from '../../times/selectors'
import { getNarrativeSelector } from '../../narrative/selectors'
import { getPatientMedicationsSelector } from '../../patientMedications/selectors'
import { getPatientAllergiesSelector } from '../../patientAllergies/selectors'
import { getPatientMedicalHistorySelector } from '../../patientMedicalHistory/selectors'
import { PatientMedicalHistoryLayoutOptionsConstants } from '../../patientMedicalHistory/store/patientMedicalHistoryLayoutOptions.constants'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { getPatientTimelineSelector } from '../selectors/patientTimeline.selector'
import { IPcrSummary } from '../../home/store/home.types'
import { useDarkMode } from '../../../hooks/useDarkMode'

const TripInformationSection: React.FC = () => {
  const pcr: IPcrSummary | null = useSelector(getPcrSelector)
  const tripInfo = useSelector(getTripInformationSelector)
  const patientDemographics = useSelector(getPatientDemographicsSelector)
  const user = useSelector(getUserSelector)
  const patientDemographicsOptions = useSelector(getPatientDemographicsOptionsSelector)
  const darkMode = useDarkMode()
  const headerTitleColor = darkMode ? '#ffffff' : '#222428'
  const narrativeState = useSelector(getNarrativeSelector)
  const patientMedicationsState = useSelector(getPatientMedicationsSelector)
  const { patientMedicationsList } = patientMedicationsState
  const { allergies } = useSelector(getPatientAllergiesSelector)
  const patientMedicalHistory = useSelector(getPatientMedicalHistorySelector)
  const configuration = useSelector(getConfigurationsSelector)
  const { serviceConfig } = configuration
  const { PR_DISPATCH_ID } = serviceConfig
  const { filteredItems } = useSelector(getPatientTimelineSelector)
  const { dispatchedDate } = useSelector(getTimesSelector)

  if (!pcr) {
    return <IonLabel>PCR Data issue!</IonLabel>
  }

  const { status } = pcr!
  const { dispatchID, dispatchedAs, disposition, dispatchPriority, patientCategory } = tripInfo
  const { dOB, age, ageUnit, firstName, lastName, gender, address1, address2, city, state, zipCode } =
    patientDemographics

  const { serviceName } = user
  const { ageUnitOptions } = patientDemographicsOptions
  const { hpi } = narrativeState

  const { medicalHistory, medicalHistoryIcdNemsisBasic } = patientMedicalHistory
  const savedMedicalHistoryList =
    serviceConfig.PR_PMHX_LAYOUT === PatientMedicalHistoryLayoutOptionsConstants.ICD_10_NEMSIS_BASIC
      ? [...medicalHistoryIcdNemsisBasic, ...medicalHistory]
      : medicalHistory

  let addressSeperator = ''
  let locationFlag = false
  if (address1 || address2 || city || state || zipCode) {
    locationFlag = true
    if ((address1 || address2 || city) && (state || zipCode)) addressSeperator = ','
  }

  const location = `${address1 || ''} ${address2 || ''} ${city?.name || ''}${addressSeperator} ${
    state ? state.name : ''
  } ${zipCode || ''}`
  const patientName = `${firstName} ${lastName}`
  let patientAge = ''
  let patientAgeUnit = ageUnit ? ageUnit.name : ''

  if (dOB && !age) {
    patientAge = getAgeFromReference(dOB, dispatchedDate || null)
    patientAgeUnit =
      ageUnitOptions.find(
        (option: { id: any }) => option.id === getAgeUnitOptionIdFromReference(dOB, dispatchedDate || null)
      )?.name ?? ''
  } else if (age) {
    patientAge = age
  }

  const medicationsData = patientMedicationsList.map((medication) => medication.name?.name)
  const allergiesData = allergies.map((allergie) => allergie.name)
  const PMHXData = savedMedicalHistoryList.map((medicalHistory) => medicalHistory.name)
  const vitalsData = filteredItems?.filter((item) => item.itemType === 'Vitals')

  let firstVital = ''
  let lastVital = ''

  const getVitalData = (data: any[], initialValue: string) => {
    return data.reduce((prevValue: string, currentValue: { key: string; value: string }, currentIndex: number) => {
      return `${prevValue}  ${currentValue.key}  :  ${currentValue.value ? currentValue.value : 'NA'}  ${
        currentIndex === 4 ? '' : ' , '
      }`
    }, initialValue)
  }

  if (vitalsData && vitalsData.length > 0) {
    const firstVitalData = vitalsData[0]
    const firstVitalInitialData = firstVitalData?.subHeader + ' ' + firstVitalData?.labelHeader + ' , '
    firstVital =
      firstVitalData?.keyValues && firstVitalData?.keyValues?.length > 0
        ? getVitalData(firstVitalData?.keyValues, firstVitalInitialData)
        : ''
  }

  if (vitalsData && vitalsData.length > 1) {
    const lastVitalData = vitalsData.slice(-1)[0]
    const lastVitalInitialData = lastVitalData?.subHeader + ' ' + lastVitalData?.labelHeader + ' , '
    lastVital =
      lastVitalData?.keyValues && lastVitalData?.keyValues?.length > 0
        ? getVitalData(lastVitalData?.keyValues, lastVitalInitialData)
        : ''
  }

  const headerSectionInfo = {
    title: 'Patient Information',
    titleColor: {
      color: headerTitleColor,
    },
    data: [
      { value: patientName, label: 'Name' },
      { value: `${dOB ? dOB : ''}`, label: 'DOB' },
      { value: `${patientAge} ${patientAgeUnit}`, label: 'Age' },
      { value: `${locationFlag ? location : ''}`, label: 'Patient Address' },
      { value: `${gender ? gender.name : ''}`, label: 'Sex' },
      { value: `${medicationsData.join(', ')}`, label: 'Medications' },
      { value: `${allergiesData.join(', ')}`, label: 'Allergies' },
      { value: `${PMHXData.join(', ')}`, label: 'PMHX' },
      { value: firstVital, label: 'First Vital' },
      { value: lastVital, label: 'Last Vital' },
      { value: `${hpi}`, label: 'HPI' },
    ],
  }

  const displayDispatchId = PR_DISPATCH_ID !== 0
  const tripSectionInfo = {
    title: 'Trip Information',
    titleColor: {
      color: headerTitleColor,
    },
    data: [
      displayDispatchId ? { value: dispatchID, label: 'Dispatch ID' } : { value: '', label: '' },
      { value: patientCategory?.name || '', label: 'Call Type' },
      { value: dispatchPriority?.name || '', label: 'Priority' },
      { value: dispatchedAs?.name || '', label: 'Dispatched As' },
      { value: disposition?.name || '', label: 'Disposition' },
      { value: status, label: 'Status' },
      { value: serviceName, label: 'Company' },
    ],
  }

  return (
    <IonList>
      <IonGrid>
        <IonRow>
          <HeaderSection isPHI qa-id='main_header_row_patient' headerSectionInfo={headerSectionInfo} />
        </IonRow>

        <hr />

        <IonRow>
          <HeaderSection isPHI qa-id='main_header_row_transport' headerSectionInfo={tripSectionInfo} />
        </IonRow>
      </IonGrid>
    </IonList>
  )
}

export default TripInformationSection
